import {
  DeleteFilled,
  EyeFilled,
  InsertRowBelowOutlined,
} from '@ant-design/icons';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import {
  FormItem,
  type IFormItemProps,
} from '@framework/form/item/FormItem.tsx';
import { Button, Space, Tooltip, Typography } from 'antd';
import { type ColumnType } from 'antd/es/table';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FlexRow } from '../../../row/FlexRow.tsx';
import { type IPickableColumn } from '../IPickableColumn.tsx';

const ColumnPickerItemTitleWrapper = styled.div`
  color: ${props => props.theme.colorText};
  font-weight: 600;
  line-height: 1.25;
  margin-top: 4px;
  max-width: 100%;

  /* min-height: 50px; */
`;

const ItemContentWrapper = styled.div`
  &&& {
    .ant-form-item .ant-checkbox-wrapper {
      margin-top: 4px;
    }
  }
`;

const layout: IFormItemProps['layout'] = {
  removeMargin: true,
  removeHelpMargin: true,
  removeLabelSpace: true,
};

export interface IColumnPickerItemProps<T> {
  column?: ColumnType<T>;
  name: string;
  editor: RelationEditor<IPickableColumn>;
  /**
   * Se true, la colonna è stata aggiunta dall'utente e non è presente di default (e.g. tag semantici)
   */
  isExtra?: boolean;
}
/**
 *
 */
export function ColumnPickerItem<T>(props: IColumnPickerItemProps<T>) {
  const { column, name, editor, isExtra } = props;
  const value = useWatch({ name }) as IPickableColumn;

  const { t } = useTranslation();

  const remove = useCallback(() => {
    if (!isExtra) return;
    const index = editor.items.findIndex(i => i.key === value.key);
    if (index >= 0) editor.removeAt(index);
  }, [editor, isExtra, value.key]);

  if (!column) return null;

  return (
    <ItemContentWrapper>
      <FlexRow
        right={
          <Space size={4}>
            {isExtra && (
              <Tooltip
                title={t(
                  'column-picker.remove-extra-column-button.tooltip',
                  'Rimuovi campo specializzato',
                )}
                placement="topLeft"
                align={{ offset: [8, -4] }}
              >
                <Button
                  aria-label={t(
                    'column-picker.remove-extra-column-button.aria-label',
                    'Rimuovi',
                  )}
                  type="link"
                  onClick={remove}
                  size="small"
                  icon={<DeleteFilled aria-hidden="true" />}
                  danger
                  style={{ paddingTop: 6 }}
                />
              </Tooltip>
            )}
            {/* escludiamo le colonne fisse dalla configurazione */}
            {/* <FormItem name={`${name}.fixed`} layout={layout}>
              <CheckboxInput
                name={`${name}.fixed`}
                children={<LockFilled />}
                disabled={!currentValue?.visible}
              />
            </FormItem> */}

            <FormItem name={`${name}.showInExpandedRow`} layout={layout}>
              <CheckboxInput
                name={`${name}.showInExpandedRow`}
                disabled={!value?.visible}
              >
                <Tooltip
                  title={t(
                    'column-picker.show-in-expanded-row.tooltip',
                    'Mostra in riga espansa',
                  )}
                >
                  <InsertRowBelowOutlined aria-hidden="true" />
                </Tooltip>
              </CheckboxInput>
            </FormItem>
          </Space>
        }
      >
        <FlexRow
          left={
            <FormItem name={`${name}.visible`} layout={layout}>
              <SwitchInput
                name={`${name}.visible`}
                size="small"
                checkedChildren={<EyeFilled aria-hidden="true" />}
              >
                <span className="sr-only">
                  {t(
                    'column-picker.switch-show-column.sr-only-text',
                    'Visibilità Campo',
                  )}
                </span>
              </SwitchInput>
            </FormItem>
          }
        >
          <ColumnPickerItemTitleWrapper>
            <Typography.Text ellipsis>
              {column.title as JSX.Element}
            </Typography.Text>
          </ColumnPickerItemTitleWrapper>
        </FlexRow>
      </FlexRow>
    </ItemContentWrapper>
  );
}
